/**
 * Plugin-side helpers for reading and writing the NyayAssist provenance
 * stamp inside a Word document's OOXML custom XML parts.
 *
 * See `AI/tools/docx_provenance.py` for the server-side counterpart and
 * the XML shape.
 */

export const PROVENANCE_NAMESPACE = 'https://nyayassist.ai/schema/draft-provenance';

export interface DraftProvenance {
  chatId: string;
  draftId: string;
  version: string;
  stampedAt?: string;
}

/**
 * Read the provenance stamp from the currently-open Word document.
 * Returns null when no NyayAssist custom XML part exists.
 */
export async function readDraftProvenance(): Promise<DraftProvenance | null> {
  try {
    return await Word.run(async (context) => {
      const parts = context.document.customXmlParts.getByNamespace(PROVENANCE_NAMESPACE);
      parts.load('items/id');
      await context.sync();
      if (parts.items.length === 0) return null;
      const part = parts.items[0];
      const xml = part.getXml();
      await context.sync();
      return parseProvenanceXml(xml.value);
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[provenance] read failed', err);
    return null;
  }
}

/**
 * Stamp the current Word document with a fresh provenance part. Removes
 * any previous part in our namespace first so there's only ever one.
 * Called from the apply pipeline so locally-applied DOCX get the stamp
 * without needing a round-trip through the server.
 */
export async function stampDraftProvenance(p: DraftProvenance): Promise<void> {
  try {
    await Word.run(async (context) => {
      const existing = context.document.customXmlParts.getByNamespace(PROVENANCE_NAMESPACE);
      existing.load('items');
      await context.sync();
      for (const part of existing.items) {
        part.delete();
      }
      await context.sync();

      const xml =
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<draft xmlns="${PROVENANCE_NAMESPACE}">` +
        `<chat_id>${escapeXml(p.chatId)}</chat_id>` +
        `<draft_id>${escapeXml(p.draftId)}</draft_id>` +
        `<version>${escapeXml(p.version)}</version>` +
        `<stamped_at>${new Date().toISOString()}</stamped_at>` +
        `</draft>`;
      context.document.customXmlParts.add(xml);
      await context.sync();
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[provenance] stamp failed', err);
  }
}

function parseProvenanceXml(xml: string): DraftProvenance | null {
  const chatId = matchTag(xml, 'chat_id');
  const draftId = matchTag(xml, 'draft_id');
  const version = matchTag(xml, 'version');
  if (!chatId) return null;
  return {
    chatId,
    draftId: draftId || chatId,
    version: version || '1',
    stampedAt: matchTag(xml, 'stamped_at') || undefined,
  };
}

function matchTag(xml: string, tag: string): string {
  const re = new RegExp(`<${tag}>([^<]*)</${tag}>`);
  return re.exec(xml)?.[1]?.trim() ?? '';
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
